import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export const MAX_LOOKALIKE_SEEDS = 5;

// The conversation outlives a reload of the tab.
const PERSISTED_KEYS = ['messages', 'extractedParams', 'previewResults', 'previewCounts', 'lookalikeSeeds', 'phase'];

/**
 * Zustand store for the agentic search chat widget. The conversation is kept in the tab's
 * sessionStorage; panel visibility and in-flight flags are not.
 */
export const useAgenticSearchStore = create(
	persist(
		(set, get) => ({
			// Panel visibility
			isOpen: false,
			setIsOpen: (val) => set({ isOpen: val }),
			toggleOpen: () => set((s) => ({ isOpen: !s.isOpen })),

			// Chat messages: { id, role: 'user'|'assistant', content, type: 'text'|'preview'|'action' }
			messages: [],
			addMessage: (msg) =>
				set((s) => ({
					messages: [
						...s.messages,
						{ id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, ...msg },
					],
				})),
			updateLastAssistantMessage: (content) =>
				set((s) => {
					const msgs = [...s.messages];
					for (let i = msgs.length - 1; i >= 0; i--) {
						if (msgs[i].role === 'assistant' && msgs[i].type === 'text') {
							msgs[i] = { ...msgs[i], content };
							break;
						}
					}
					return { messages: msgs };
				}),

			// Streaming state
			isStreaming: false,
			streamingContent: '',
			setStreaming: (val) => set({ isStreaming: val, ...(val ? {} : { streamingContent: '' }) }),
			appendStreamContent: (chunk) => set((s) => ({ streamingContent: s.streamingContent + chunk })),

			// Extracted search params from AI
			extractedParams: null,
			setExtractedParams: (params) => set({ extractedParams: params }),

			// Preview state
			previewResults: null,
			setPreviewResults: (results) => set({ previewResults: results }),
			isLoadingPreview: false,
			setIsLoadingPreview: (val) => set({ isLoadingPreview: val }),

			// Last preview: { total, stages, hiddenByCompanyKind?, warnings? } | null
			previewCounts: null,
			setPreviewCounts: (counts) => set({ previewCounts: counts }),

			// Example companies the user picked for a lookalike search: [{ id, name }]
			lookalikeSeeds: [],
			addLookalikeSeed: (seed) =>
				set((s) =>
					s.lookalikeSeeds.length >= MAX_LOOKALIKE_SEEDS || s.lookalikeSeeds.some(({ id }) => id === seed.id)
						? {}
						: { lookalikeSeeds: [...s.lookalikeSeeds, { id: seed.id, name: seed.name }] },
				),
			removeLookalikeSeed: (id) =>
				set((s) => ({ lookalikeSeeds: s.lookalikeSeeds.filter((seed) => seed.id !== id) })),

			// Chat phase: idle → conversing → awaiting_approval (maps) → previewing (hr) → executing → completed
			phase: 'idle',
			setPhase: (phase) => set({ phase }),

			// Reset everything for a new conversation
			resetChat: () =>
				set({
					messages: [],
					isStreaming: false,
					streamingContent: '',
					extractedParams: null,
					previewResults: null,
					isLoadingPreview: false,
					previewCounts: null,
					lookalikeSeeds: [],
					phase: 'idle',
				}),

			/**
			 * Get messages in the format expected by the backend (for conversation history).
			 * Only includes text messages, strips IDs and types. The last preview's counts (with a too-broad
			 * full-text warning and the number of hidden holdings, when present) and the picked example
			 * companies follow as system messages: the assistant narrows a too-broad full text and sets lookalikeSeeds from the ids it is given.
			 */
			getMessagesForApi: () => {
				const { messages, previewCounts, lookalikeSeeds } = get();
				const history = messages
					.filter((m) => m.type === 'text')
					.map(({ role, content }) => ({ role, content }));
				if (previewCounts) {
					history.push({ role: 'system', content: `[preview_counts]${JSON.stringify(previewCounts)}` });
				}
				if (lookalikeSeeds.length > 0) {
					history.push({ role: 'system', content: `[lookalike_seeds]${JSON.stringify(lookalikeSeeds)}` });
				}
				return history;
			},
		}),
		{
			name: 'b2bhero:agentic-search-chat',
			storage: createJSONStorage(() => sessionStorage),
			partialize: (state) => Object.fromEntries(PERSISTED_KEYS.map((key) => [key, state[key]])),
		},
	),
);
